/**
 * Metadata Governance Configuration - Single Source of Truth
 * 
 * GOVERNANCE REDESIGN (2025-01):
 * This file is the SINGLE SOURCE OF TRUTH for:
 * 1. COMPLETENESS - Fields that count toward operational readiness %
 * 2. COMPLIANCE - Fields required for SEO/AIO/Schema standards
 * 3. FIELD CONSTRAINTS - Character limits (minLength, maxLength) per field
 * 4. ENTITY QUALIFICATION - filterCondition and filterDescription per entity
 * 5. ENTITY EXCLUSIONS - Which fields don't apply per entity (with reasons)
 * 
 * Field Treatment Modes:
 * - REQUIRED_FOR_COMPLETENESS: Counts toward completion %, user-facing/machine-critical
 * - REQUIRED_FOR_COMPLIANCE: Required by standards, validated separately, NOT in completion %
 * - DERIVED_COMPUTED: Never authored, computed at runtime from canonical fields
 * - NOT_APPLICABLE: Excluded for specific entity types
 * 
 * @see docs/METADATA_GOVERNANCE.md for full documentation
 */

import type { LocalizationContentType } from '@/constants/localization';

// ============================================================================
// PROFILE BIO CONSTRAINTS (exported for form validation)
// ============================================================================

/**
 * Profile bio character limits for SEO/AIO/localization quality
 * These are the mandatory gates for triggering translation pipeline.
 * Must match the edge function constants in supabase/functions/_shared/config/field-compliance-config.ts
 */
export const PROFILE_BIO_MIN_LENGTH = 150;      // For new users - optimal for SEO meta descriptions
export const PROFILE_BIO_MAX_LENGTH = 500;      // Hard cap to prevent verbosity
export const PROFILE_BIO_LEGACY_MIN = 50;       // Legacy threshold for enrichment eligibility
export const PROFILE_BIO_WORD_GUIDANCE = { min: 30, max: 80 };  // Approximate word count guidance

// ============================================================================
// TREATMENT MODES
// ============================================================================

export type TreatmentMode = 
  | 'REQUIRED_FOR_COMPLETENESS'  // 🟢 Counts toward completion %
  | 'REQUIRED_FOR_COMPLIANCE'    // 🔵 Standards-required, validated separately
  | 'DERIVED_COMPUTED'           // 🟡 Computed at runtime, never tracked
  | 'NOT_APPLICABLE';            // 🔴 Excluded for this entity

export type ComplianceCategory = 'seo' | 'aio' | 'social' | 'schema' | 'content';

export interface FieldGovernanceRule {
  field: string;
  treatmentMode: TreatmentMode;
  category: 'source' | 'content' | 'seo' | 'opengraph' | 'aio' | 'schema' | 'derived';
  source: 'sql' | 'ai' | 'runtime';
  localizable: boolean;
  complianceStandard?: string;  // e.g., 'SEO', 'OpenGraph', 'Schema.org', 'hreflang'
  derivedFrom?: string;         // For derived fields, the canonical source
  sourceLocaleOnly?: boolean;   // If true, only enforced for EN source locale
  // Character limit constraints (Google 2025 SEO standards)
  minLength?: number;
  maxLength?: number;
  // Human-readable descriptions
  label?: string;
  purpose?: string;
  seoImpact?: string;
  aioImpact?: string;
  socialImpact?: string;
  schemaImpact?: string;
  complianceCategories?: ComplianceCategory[];
  isRequired?: boolean;
}

// ============================================================================
// COMPLETENESS FIELDS (🟢)
// Only these fields count toward the completion percentage
// ============================================================================

export const COMPLETENESS_FIELDS = [
  // Content fields - user-facing, must be present for entity to be consumable
  'translated_name',          // Name/Title/Question - except profile (names not translated)
  'translated_body',          // Bio/Answer/Content - except on-demand entities
  'translated_body_short',    // Excerpt/Summary/Tagline - for cards, previews, route taglines
  'translated_slug',          // URL slug - for indexable entities with standalone pages
  
  // SEO fields - required for search engine visibility
  'meta_title',              // <title> tag
  'meta_description',        // meta description
  
  // NOTE: og_description moved to DERIVED_FIELDS - truncate(meta_description, 155)
  // NOTE: EN row is the authoritative SOT (2026-02) - no original_name/original_slug columns
  
  // AIO fields - required for AI/voice search visibility
  'aio_snippet',             // AI overview/voice search snippet
  'faq_items',               // FAQ structured data (≥3 Q&A pairs)
  'schema_about',            // Schema.org about field (profiles)
] as const;

export type CompletenessField = typeof COMPLETENESS_FIELDS[number];

// ============================================================================
// COMPLIANCE FIELDS (🔵)
// Required by standards but NOT counted toward completion %
// Validated in separate compliance dashboard
// ============================================================================

export const COMPLIANCE_FIELDS = [
  // NOTE: schema_about promoted to COMPLETENESS_FIELDS (2026-02) - required for profiles
  // NOTE: original_slug REMOVED (2026-02) - EN row's translated_slug is the authoritative source
  // NOTE: meta_keywords REMOVED - deprecated by Google since 2009
  // All compliance fields are now in COMPLETENESS - this array is intentionally empty
] as const;

export type ComplianceField = typeof COMPLIANCE_FIELDS[number];

// ============================================================================
// DERIVED FIELDS (🟡)
// Always computed at runtime, never stored or tracked
// ============================================================================

export const DERIVED_FIELDS = [
  // OpenGraph derived
  'og_title',                // truncate(meta_title, 60)
  'og_description',          // truncate(meta_description, 155)
  'og_image_alt',            // "Tax.Network - {translated_name}"
  'og_locale',               // locale.replace('-', '_')
  'og_type',                 // 'website' or 'publication' based on source_type
  
  // URL derived
  'canonical_url',           // SITE_URL + translated_slug
  
  // Twitter derived (same as OG)
  'twitter_image_alt',       // = og_image_alt
  'twitter_image_url',       // = og_image_url
  
  // Navigation derived
  'breadcrumb_label',        // = translated_name (or short)
  
  // Schema derived
  'schema_type',             // Derived from entity type via config
  'schema_author',           // Derived from author data
  'schema_audience',         // Derived from entity type via config
  
  // NOTE: translated_primary_short moved to COMPLETENESS_FIELDS for routes/locations/topics
  // It requires AI generation, not simple truncation
] as const;

export type DerivedField = typeof DERIVED_FIELDS[number];

// ============================================================================
// FIELD GOVERNANCE RULES (Complete Mapping with Character Limits)
// Google 2025 SEO/AIO standards
// ============================================================================

export const FIELD_GOVERNANCE: FieldGovernanceRule[] = [
  // === COMPLETENESS FIELDS (🟢) ===
  { 
    field: 'translated_name', 
    treatmentMode: 'REQUIRED_FOR_COMPLETENESS', 
    category: 'content', 
    source: 'ai', 
    localizable: true,
    label: 'Name/Title',
    complianceCategories: ['seo', 'aio', 'social', 'schema', 'content'],
    purpose: 'Main identifier shown in search results, social cards, and schema markup. EN row is authoritative SOT.',
    seoImpact: 'Primary ranking signal - appears in title tag, H1, breadcrumbs',
    aioImpact: 'Voice search answers use this as entity name',
    socialImpact: 'Appears in OG title and Twitter cards when shared',
    schemaImpact: 'Used as schema.org name property',
    minLength: 5,
    maxLength: 60,
    isRequired: true,
  },
  // NOTE: EN row is the authoritative SOT (2026-02) - no original_name/original_slug columns
  { 
    field: 'translated_body', 
    treatmentMode: 'REQUIRED_FOR_COMPLETENESS', 
    category: 'content', 
    source: 'ai', 
    localizable: true,
    label: 'Full Body Content',
    complianceCategories: ['content', 'schema'],
    purpose: 'Full content for page rendering and in-depth information',
    seoImpact: 'Crawled for keyword relevance but not in meta tags',
    aioImpact: 'Source for generating speakable content',
    socialImpact: 'Not directly used in social cards',
    schemaImpact: 'Used for articleBody in Publication schema',
    minLength: 100,
    isRequired: true,
  },
  { 
    field: 'translated_body_short', 
    treatmentMode: 'REQUIRED_FOR_COMPLETENESS', 
    category: 'content', 
    source: 'ai', 
    localizable: true,
    label: 'Short Description/Excerpt',
    complianceCategories: ['seo', 'aio', 'social', 'content'],
    purpose: 'Concise summary for search snippets and AI responses',
    seoImpact: 'Primary meta description source, affects click-through rate',
    aioImpact: 'Used as llm_summary for AI assistant responses',
    socialImpact: 'Falls back to this if og_description is empty',
    schemaImpact: 'Used as description in schema.org',
    minLength: 50,
    maxLength: 160,
    isRequired: true,
  },
  { 
    field: 'translated_slug', 
    treatmentMode: 'REQUIRED_FOR_COMPLETENESS', 
    category: 'content', 
    source: 'ai', 
    localizable: true,
    label: 'URL Slug',
    complianceCategories: ['seo'],
    purpose: 'Localized URL path segment for SEO-friendly URLs',
    seoImpact: 'Appears in URL, affects keyword relevance and click-through',
    aioImpact: 'Not used in voice responses',
    socialImpact: 'Visible in shared links',
    schemaImpact: 'Used to construct canonical URL',
    minLength: 3,
    maxLength: 80,
    isRequired: true,
  },
  { 
    field: 'meta_title', 
    treatmentMode: 'REQUIRED_FOR_COMPLETENESS', 
    category: 'seo', 
    source: 'ai', 
    localizable: true, 
    complianceStandard: 'SEO',
    label: 'Meta Title',
    complianceCategories: ['seo'],
    purpose: 'HTML <title> tag content for search engine results pages',
    seoImpact: 'Primary ranking signal - exact text shown in SERP',
    aioImpact: 'May be used by AI to identify page topic',
    socialImpact: 'Fallback if og_title is empty',
    schemaImpact: 'Not used in schema (name property used instead)',
    minLength: 30,
    maxLength: 60,
    isRequired: true,
  },
  { 
    field: 'meta_description', 
    treatmentMode: 'REQUIRED_FOR_COMPLETENESS', 
    category: 'seo', 
    source: 'ai', 
    localizable: true, 
    complianceStandard: 'SEO',
    label: 'Meta Description',
    complianceCategories: ['seo'],
    purpose: 'HTML meta description shown in search results',
    seoImpact: 'Affects click-through rate, may be overridden by Google',
    aioImpact: 'May be used for AI context',
    socialImpact: 'Fallback if og_description is empty',
    schemaImpact: 'Not used in schema (description property used instead)',
    minLength: 70,
    maxLength: 160,
    isRequired: true,
  },
  // NOTE: og_description moved to DERIVED_FIELDS - truncate(meta_description, 155)
  { 
    field: 'og_description', 
    treatmentMode: 'DERIVED_COMPUTED', 
    category: 'derived', 
    source: 'runtime', 
    localizable: true, 
    derivedFrom: 'meta_description',
    label: 'OG Description',
    complianceCategories: ['social'],
    purpose: 'Description shown when content is shared on social platforms (derived from meta_description)',
    seoImpact: 'Indirect - better descriptions increase shares',
    aioImpact: 'Not used by AI assistants',
    socialImpact: 'Shown below title in social cards',
    schemaImpact: 'Not used in schema',
    maxLength: 155,
  },
  { 
    field: 'aio_snippet', 
    treatmentMode: 'REQUIRED_FOR_COMPLETENESS', 
    category: 'aio', 
    source: 'ai', 
    localizable: true, 
    complianceStandard: 'AIO',
    label: 'AIO Snippet',
    complianceCategories: ['aio'],
    purpose: 'Pre-written answer optimized for AI assistants and voice search',
    seoImpact: 'Can become featured snippet in search results',
    aioImpact: 'Direct answer for ChatGPT, Google SGE, voice assistants',
    socialImpact: 'Not used in social cards',
    schemaImpact: 'Used in speakable schema',
    minLength: 50,
    maxLength: 300,
    isRequired: true,
  },
  { 
    field: 'faq_items', 
    treatmentMode: 'REQUIRED_FOR_COMPLETENESS', 
    category: 'aio', 
    source: 'ai', 
    localizable: true, 
    complianceStandard: 'Schema.org',
    label: 'FAQ Items',
    complianceCategories: ['aio', 'schema'],
    purpose: 'Question-answer pairs for rich snippets and AI training',
    seoImpact: 'Can generate FAQ rich snippets in SERP',
    aioImpact: 'Directly answers common user questions in AI responses',
    socialImpact: 'Not used in social cards',
    schemaImpact: 'Generates FAQPage schema for rich results',
    isRequired: true,
  },
  
  // === SCHEMA COMPLETENESS FIELDS (promoted from compliance 2026-02) ===
  { 
    field: 'schema_about', 
    treatmentMode: 'REQUIRED_FOR_COMPLETENESS', 
    category: 'schema', 
    source: 'ai', 
    localizable: true, 
    complianceStandard: 'Schema.org',
    label: 'Schema About',
    complianceCategories: ['schema', 'aio'],
    purpose: 'Detailed about description for schema.org markup',
    seoImpact: 'Helps Google understand entity topic in knowledge graph',
    aioImpact: 'Provides entity context for AI understanding',
    socialImpact: 'Not used in social cards',
    schemaImpact: 'Used as about property in schema.org',
    minLength: 50,
    maxLength: 500,
    isRequired: true,
  },
  // NOTE: original_slug REMOVED (2026-02) - EN row's translated_slug is the authoritative source
  
  // NOTE: meta_keywords REMOVED - Google deprecated this in 2009, zero SEO value
  
  // === DERIVED FIELDS (🟡) ===
  { field: 'og_title', treatmentMode: 'DERIVED_COMPUTED', category: 'derived', source: 'runtime', localizable: true, derivedFrom: 'meta_title', label: 'OG Title', maxLength: 60 },
  { field: 'og_image_alt', treatmentMode: 'DERIVED_COMPUTED', category: 'derived', source: 'runtime', localizable: true, derivedFrom: 'translated_name', label: 'OG Image Alt', maxLength: 125 },
  { field: 'og_locale', treatmentMode: 'DERIVED_COMPUTED', category: 'derived', source: 'runtime', localizable: false, derivedFrom: 'locale' },
  { field: 'og_type', treatmentMode: 'DERIVED_COMPUTED', category: 'derived', source: 'runtime', localizable: false, derivedFrom: 'source_type' },
  { field: 'canonical_url', treatmentMode: 'DERIVED_COMPUTED', category: 'derived', source: 'runtime', localizable: true, derivedFrom: 'translated_slug' },
  { field: 'breadcrumb_label', treatmentMode: 'DERIVED_COMPUTED', category: 'derived', source: 'runtime', localizable: true, derivedFrom: 'translated_name' },
  { field: 'speakable_content', treatmentMode: 'DERIVED_COMPUTED', category: 'derived', source: 'runtime', localizable: true, derivedFrom: 'aio_snippet' },
  { field: 'llm_summary', treatmentMode: 'DERIVED_COMPUTED', category: 'derived', source: 'runtime', localizable: true, derivedFrom: 'translated_body_short' },
  { field: 'schema_type', treatmentMode: 'DERIVED_COMPUTED', category: 'derived', source: 'runtime', localizable: false, derivedFrom: 'source_type' },
  { field: 'schema_author', treatmentMode: 'DERIVED_COMPUTED', category: 'derived', source: 'runtime', localizable: false, derivedFrom: 'author' },
  { field: 'schema_audience', treatmentMode: 'DERIVED_COMPUTED', category: 'derived', source: 'runtime', localizable: false, derivedFrom: 'source_type' },
  
  // Entity fields - SQL-seeded infrastructure metadata, NOT tracked for completeness
  // Populated by seeders, identical across locales, not AI-generated or translated
  { 
    field: 'entity_fields', 
    treatmentMode: 'NOT_APPLICABLE', 
    category: 'schema', 
    source: 'sql', 
    localizable: false,
    label: 'Entity Fields',
    complianceCategories: ['schema'],
    purpose: 'Entity-specific metadata (ISO codes, schema_type, coordinates, etc.) - SQL-seeded, not content',
    seoImpact: 'Powers entity-specific schema properties (infrastructure, not content health)',
    schemaImpact: 'Populates entity-specific schema properties (geoCoordinates, addressCountry, etc.)',
    isRequired: false,
  },
];

// ============================================================================
// ENTITY QUALIFICATION RULES
// Single source of truth for which records qualify for localization
// ============================================================================

export interface EntityQualificationRule {
  entityType: LocalizationContentType;
  /** SQL WHERE clause for qualification */
  filterCondition: string | null;
  /** Human-readable description for UI tooltips */
  filterDescription: string;
  /** Note about any auditing differences (e.g., metadata-health-stats uses simplified filter) */
  auditingNote?: string;
}

export const ENTITY_QUALIFICATION_RULES: EntityQualificationRule[] = [
  {
    entityType: 'location',
    filterCondition: null,  // All active locations
    filterDescription: 'All active locations (no filter)',
  },
  {
    entityType: 'page',
    filterCondition: "editorial_status = 'published'",
    filterDescription: 'Published institutional pages',
  },
  {
    entityType: 'route',
    filterCondition: null,
    filterDescription: 'All route definitions',
  },
  {
    entityType: 'faq',
    filterCondition: 'is_active = true',
    filterDescription: 'Active FAQ entries',
  },
  {
    entityType: 'email_template',
    filterCondition: 'is_active = true',
    filterDescription: 'Active email templates',
  },
  {
    entityType: 'practice_area',
    filterCondition: null,
    filterDescription: 'All practice areas',
  },
  {
    entityType: 'topic',
    filterCondition: "is_active = true AND translation_rule_status = 'approved'",
    filterDescription: 'Active topics with approved translation rules',
  },
  {
    entityType: 'profile',
    filterCondition: "profile_listed = true AND vanity_url IS NOT NULL AND display_name IS NOT NULL AND email IS NOT NULL AND about IS NOT NULL AND LENGTH(about) >= 50",
    filterDescription: 'Listed profiles with vanity URL, display name, email, and bio ≥50 characters',
    auditingNote: 'metadata-health-stats uses about_en != \'\' for audit counting (simplified)',
  },
  {
    entityType: 'publication',
    filterCondition: "editorial_status = 'published'",
    filterDescription: 'Published publications',
  },
  {
    entityType: 'comment',
    filterCondition: "status = 'approved'",
    filterDescription: 'Approved comments',
  },
  {
    entityType: 'membership_plan',
    filterCondition: 'active = true AND location_id = 0',
    filterDescription: 'Active international plans (Brazil excluded)',
  },
];

/**
 * Get qualification rule for an entity type
 */
export function getEntityQualification(entityType: LocalizationContentType): EntityQualificationRule | undefined {
  return ENTITY_QUALIFICATION_RULES.find(r => r.entityType === entityType);
}

/**
 * Get filter description for an entity type (for UI tooltips)
 */
export function getEntityFilterDescription(entityType: LocalizationContentType): string {
  const rule = ENTITY_QUALIFICATION_RULES.find(r => r.entityType === entityType);
  return rule?.filterDescription || rule?.filterCondition || 'No filter';
}

// ============================================================================
// ENTITY-SPECIFIC EXCLUSIONS WITH REASONS
// Record<field, reason> - explains WHY fields don't apply
// ============================================================================

// DERIVED from entity-source-config.json -- Single Source of Truth
// Merges both AI and SQL exclusions to prevent drift
// Preserves Record<field, reason> format for UI tooltips
import { getEntityNonApplicableAIFields, getEntityNonApplicableSqlFields, getEntitySqlSeededFields, getAllEntityTypes } from '@/config/entitySourceConfig';

// Reason descriptions for UI tooltips (optional enrichment over the derived field list)
const EXCLUSION_REASONS: Record<string, Record<string, string>> = {
  location: {
    translated_body_short: 'Locations have no short description column - summary goes to translated_body',
  },
  topic: {
    translated_body_short: 'Topics have no short description column - description goes to translated_body',
  },
  publication: {
    translated_body: 'On-demand translation - body is translated when publication is viewed, not pre-seeded',
    translated_body_short: 'SQL-seeded from excerpt_en (not AI-generated), but IS translated to non-EN locales',
  },
  page: {
    faq_items: 'FAQs for pages are embedded in content body',
  },
  route: {
    translated_body: 'Routes are navigation structures, they have no body content',
    faq_items: 'Routes don\'t have FAQ structure',
  },
  profile: {
    translated_slug: 'Runtime: = users.vanity_url (invariant across all 36 locales)',
    translated_name: 'Runtime: = users.display_name (invariant across all 36 locales)',
    meta_title: 'Runtime: = users.display_name (invariant across all 36 locales)',
    translated_body_short: 'Derived from meta_description at runtime',
    faq_items: 'Profiles do not have FAQ schema',
  },
  faq: {
    translated_body_short: 'FAQ answers need full context, excerpts would lose meaning',
    translated_slug: 'FAQs are embedded in parent pages, they have no standalone URLs',
    meta_title: 'Parent page handles SEO title',
    meta_description: 'Parent page handles meta description',
    aio_snippet: 'The answer field IS the AIO-optimized response',
    faq_items: 'This entity IS a FAQ item itself',
    schema_about: 'FAQ schema uses question/acceptedAnswer properties',
    entity_fields: 'FAQs have no entity-specific metadata',
  },
  comment: {
    translated_name: 'Comments have no title - they are responses to publications',
    translated_body_short: 'Full comment is always displayed',
    translated_slug: 'Comments are inline on publication pages',
    meta_title: 'Comments are not indexed as separate pages',
    meta_description: 'Comments inherit publication SEO context',
    aio_snippet: 'Comments are user-generated content',
    faq_items: 'Comments are discussion, not structured Q&A',
    schema_about: 'Comments use Comment schema with simpler properties',
    entity_fields: 'Comments have no entity-specific metadata',
  },
  practice_area: {
    translated_body_short: 'Practice areas have no description content to excerpt',
    translated_body: 'Practice areas are simple labels with no body content',
    translated_slug: 'Practice areas are filter/dropdown values, not indexed pages',
    meta_title: 'Practice areas are not indexed as separate pages',
    meta_description: 'Practice areas are embedded in profiles',
    aio_snippet: 'Practice areas are simple labels',
    faq_items: 'Practice areas are not FAQ content',
    schema_about: 'Practice areas are simple category values',
    entity_fields: 'Practice areas have no entity-specific metadata',
  },
  email_template: {
    translated_slug: 'Email templates use template_type as identifier, not URLs',
    meta_title: 'Emails are transactional, not indexed by search engines',
    meta_description: 'Emails are not web pages, no meta description needed',
    aio_snippet: 'Emails are transactional, no AI snippet needed',
    faq_items: 'Emails are not FAQ content',
    schema_about: 'Emails are not structured schema content',
  },
  membership_plan: {
    translated_body_short: 'Plans use feature bullet arrays, not excerpts',
    translated_slug: 'Plans are embedded in pricing page, not standalone URLs',
    meta_title: 'Plans are not indexed as separate pages',
    meta_description: 'Plans are embedded in pricing page',
    aio_snippet: 'Plans are transactional, no AI snippet needed',
    faq_items: 'Plans are not FAQ content',
    schema_about: 'Plans use Product schema from pricing page',
    entity_fields: 'Plans have no entity-specific metadata beyond tier/location_id',
  },
};

// Build ENTITY_EXCLUDED_FIELDS by deriving field list from JSON SOT + attaching reasons
export const ENTITY_EXCLUDED_FIELDS: Record<string, Record<string, string>> = Object.fromEntries(
  (getAllEntityTypes() as string[]).map(type => {
    const aiExcluded = getEntityNonApplicableAIFields(type as LocalizationContentType);
    const sqlExcluded = getEntityNonApplicableSqlFields(type as LocalizationContentType);
    const sqlSeeded = getEntitySqlSeededFields(type as LocalizationContentType);
    // Exclude AI-inapplicable fields UNLESS they are SQL-seeded
    // (SQL-seeded fields like publication excerpt are translatable and must stay visible)
    const trulyExcludedAI = aiExcluded.filter(f => !sqlSeeded.includes(f));
    const allExcluded = [...new Set([...trulyExcludedAI, ...sqlExcluded])];
    const reasons = EXCLUSION_REASONS[type] || {};
    const fieldMap: Record<string, string> = {};
    for (const field of allExcluded) {
      fieldMap[field] = reasons[field] || `Not applicable for ${type}`;
    }
    return [type, fieldMap];
  })
) as Record<LocalizationContentType, Record<string, string>>;

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get field governance rule by field name
 */
export function getFieldGovernance(field: string): FieldGovernanceRule | undefined {
  return FIELD_GOVERNANCE.find(r => r.field === field);
}

/**
 * Get static field character constraints from FIELD_GOVERNANCE
 * 
 * NOTE: For script-aware constraints (CJK, RTL, etc.), use:
 * getFieldConstraints(entityType, field, scriptType) from qualityConfig.ts
 * 
 * @deprecated Prefer getFieldConstraints() from qualityConfig.ts for entity-aware limits
 */
export function getStaticFieldLimits(field: string): { minLength?: number; maxLength?: number } {
  const rule = FIELD_GOVERNANCE.find(r => r.field === field);
  return { minLength: rule?.minLength, maxLength: rule?.maxLength };
}

/**
 * Check if a field counts toward completion percentage
 */
export function isCompletenessField(field: string): boolean {
  return (COMPLETENESS_FIELDS as readonly string[]).includes(field);
}

/**
 * Check if a field is compliance-only (validated but not in completion %)
 */
export function isComplianceOnlyField(field: string): boolean {
  return (COMPLIANCE_FIELDS as readonly string[]).includes(field);
}

/**
 * Check if a field is derived (computed at runtime)
 */
export function isDerivedField(field: string): boolean {
  return (DERIVED_FIELDS as readonly string[]).includes(field);
}

/**
 * Get completeness fields applicable to an entity type
 */
export function getCompletenessFieldsForEntity(entityType: LocalizationContentType): string[] {
  const excluded = Object.keys(ENTITY_EXCLUDED_FIELDS[entityType] || {});
  return COMPLETENESS_FIELDS.filter(field => !excluded.includes(field));
}

// ============================================================================
// AI-OPTIMIZABLE FIELDS
// ============================================================================

/**
 * Fields excluded from AI optimization (too large or complex)
 * Shared constant between frontend and backend - MUST stay in sync with
 * supabase/functions/_shared/config/metadata-field-definitions.ts
 */
export const AI_EXCLUDED_FIELDS = [
  'translated_body',  // Full body content - too large for inline AI optimization
  'faq_items',        // Array field - requires specialized handling
] as const;

/**
 * Get fields that can be AI-optimized for an entity type
 * 
 * Uses COMPLETENESS_FIELDS minus:
 * - Entity-specific exclusions (ENTITY_EXCLUDED_FIELDS)
 * - AI-excluded fields (translated_body, faq_items)
 * 
 * This is the SINGLE source of truth for "what fields can the AI Fix button fix"
 * Used by OptimizeButton (frontend) and qa-optimize-entity (backend)
 */
export function getOptimizableFieldsForEntity(entityType: LocalizationContentType): string[] {
  const excluded = Object.keys(ENTITY_EXCLUDED_FIELDS[entityType] || {});
  
  return COMPLETENESS_FIELDS.filter(field => 
    !excluded.includes(field) && 
    !AI_EXCLUDED_FIELDS.includes(field as typeof AI_EXCLUDED_FIELDS[number])
  );
}

/**
 * Get compliance fields applicable to an entity type
 * NOTE: original_slug removed (2026-02) - EN row's translated_slug is the authoritative source
 */
export function getComplianceFieldsForEntity(entityType: LocalizationContentType, _locale?: string): string[] {
  const excluded = Object.keys(ENTITY_EXCLUDED_FIELDS[entityType] || {});
  return COMPLIANCE_FIELDS.filter(field => !excluded.includes(field));
}

/**
 * Check if a field is excluded for an entity type
 */
export function isFieldExcludedForEntity(entityType: LocalizationContentType, field: string): boolean {
  return field in (ENTITY_EXCLUDED_FIELDS[entityType] || {});
}

/**
 * Get exclusion reason for a field × entity combination
 */
export function getFieldExclusionReason(entityType: LocalizationContentType, field: string): string | null {
  return ENTITY_EXCLUDED_FIELDS[entityType]?.[field] || null;
}

/**
 * Get treatment mode for a field×entity combination
 */
export function getFieldTreatmentMode(field: string, entityType: LocalizationContentType): TreatmentMode {
  // Check if excluded for this entity
  if (isFieldExcludedForEntity(entityType, field)) {
    return 'NOT_APPLICABLE';
  }
  
  // Check derived first
  if (isDerivedField(field)) {
    return 'DERIVED_COMPUTED';
  }
  
  // Check completeness
  if (isCompletenessField(field)) {
    return 'REQUIRED_FOR_COMPLETENESS';
  }
  
  // Check compliance
  if (isComplianceOnlyField(field)) {
    return 'REQUIRED_FOR_COMPLIANCE';
  }
  
  // Default to not applicable
  return 'NOT_APPLICABLE';
}

// ============================================================================
// COMPLIANCE VALIDATION RULES
// ============================================================================

export interface ComplianceValidationRule {
  field: string;
  standard: string;
  validationFn: (value: unknown) => boolean;
  errorMessage: string;
}

export const COMPLIANCE_VALIDATION_RULES: ComplianceValidationRule[] = [
  // NOTE: meta_keywords removed - deprecated by Google since 2009, zero SEO value
  // NOTE: original_slug removed (2026-02) - EN row's translated_slug is the authoritative source
  {
    field: 'schema_about',
    standard: 'Schema.org',
    validationFn: (value) => typeof value === 'string' && value.trim().length > 0,
    errorMessage: 'schema_about must be a non-empty string',
  },
  {
    field: 'faq_items',
    standard: 'Schema.org',
    validationFn: (value) => Array.isArray(value) && value.length >= 3,
    errorMessage: 'faq_items must have at least 3 Q&A pairs for FAQPage schema',
  },
];

/**
 * Validate a field value against compliance rules
 */
export function validateCompliance(field: string, value: unknown): { valid: boolean; error?: string } {
  const rule = COMPLIANCE_VALIDATION_RULES.find(r => r.field === field);
  if (!rule) {
    return { valid: true };  // No rule means no validation required
  }
  
  const valid = rule.validationFn(value);
  return valid ? { valid: true } : { valid: false, error: rule.errorMessage };
}

// ============================================================================
// ENTITY TIER CLASSIFICATION
// ============================================================================

export type EntityTier = 
  | 'FULL_SEO_AIO_SCHEMA'    // Tier 1: location, topic, publication, page, profile
  | 'LIMITED_SEO'            // Tier 2: route
  | 'TRANSLATION_ONLY'       // Tier 3: faq, email_template, practice_area
  | 'ON_DEMAND';             // Tier 4: comment

export const ENTITY_TIER_MAP: Record<LocalizationContentType, EntityTier> = {
  location: 'FULL_SEO_AIO_SCHEMA',
  topic: 'FULL_SEO_AIO_SCHEMA',
  publication: 'FULL_SEO_AIO_SCHEMA',
  page: 'FULL_SEO_AIO_SCHEMA',
  profile: 'FULL_SEO_AIO_SCHEMA',
  route: 'LIMITED_SEO',
  faq: 'TRANSLATION_ONLY',
  email_template: 'TRANSLATION_ONLY',
  practice_area: 'TRANSLATION_ONLY',
  membership_plan: 'TRANSLATION_ONLY',
  content_type: 'TRANSLATION_ONLY',
  comment: 'ON_DEMAND',
};

export function getEntityTier(entityType: LocalizationContentType): EntityTier {
  return ENTITY_TIER_MAP[entityType];
}

/**
 * Check if an entity requires SEO fields
 */
export function entityRequiresSEO(entityType: LocalizationContentType): boolean {
  const tier = ENTITY_TIER_MAP[entityType];
  return tier === 'FULL_SEO_AIO_SCHEMA' || tier === 'LIMITED_SEO';
}

/**
 * Check if an entity requires AIO fields
 */
export function entityRequiresAIO(entityType: LocalizationContentType): boolean {
  const tier = ENTITY_TIER_MAP[entityType];
  return tier === 'FULL_SEO_AIO_SCHEMA' || tier === 'LIMITED_SEO';
}

/**
 * Check if an entity requires Schema.org fields
 */
export function entityRequiresSchema(entityType: LocalizationContentType): boolean {
  const tier = ENTITY_TIER_MAP[entityType];
  return tier === 'FULL_SEO_AIO_SCHEMA' || tier === 'LIMITED_SEO';
}

// ============================================================================
// TOOLTIP HELPERS
// ============================================================================

/**
 * Get tooltip explanation for why a field is included or excluded for an entity
 * Returns a rich explanation suitable for UI tooltips
 */
export function getFieldTooltip(entityType: LocalizationContentType, field: string): string {
  const exclusion = ENTITY_EXCLUDED_FIELDS[entityType]?.[field];
  if (exclusion) {
    return `❌ Excluded: ${exclusion}`;
  }
  
  const rule = FIELD_GOVERNANCE.find(r => r.field === field);
  if (!rule) {
    return 'Unknown field';
  }
  
  const categories = (rule.complianceCategories || []).map(c => c.toUpperCase()).join(', ');
  const limits = [];
  if (rule.minLength) limits.push(`Min: ${rule.minLength} chars`);
  if (rule.maxLength) limits.push(`Max: ${rule.maxLength} chars`);
  const limitStr = limits.length > 0 ? `\n\n📏 ${limits.join(' | ')}` : '';
  
  return `✅ ${rule.label || field} [${categories}]

${rule.purpose || ''}

🔍 SEO: ${rule.seoImpact || 'N/A'}
🤖 AIO: ${rule.aioImpact || 'N/A'}
📱 Social: ${rule.socialImpact || 'N/A'}
📊 Schema: ${rule.schemaImpact || 'N/A'}${limitStr}`;
}

/**
 * Get short tooltip for compact displays (e.g., matrix cells)
 */
export function getFieldTooltipShort(entityType: LocalizationContentType, field: string): string {
  const exclusion = ENTITY_EXCLUDED_FIELDS[entityType]?.[field];
  if (exclusion) {
    return `Excluded: ${exclusion}`;
  }
  
  const rule = FIELD_GOVERNANCE.find(r => r.field === field);
  if (!rule) return 'Unknown field';
  
  return rule.purpose || rule.label || field;
}

/**
 * Get all fields with their governance info for an entity type
 */
export function getEntityFieldsWithCompliance(entityType: LocalizationContentType): Array<{
  field: string;
  info: FieldGovernanceRule;
  isApplicable: boolean;
  exclusionReason: string | null;
}> {
  return FIELD_GOVERNANCE
    .filter(rule => rule.treatmentMode !== 'DERIVED_COMPUTED')  // Skip derived fields
    .map((rule) => {
      const exclusionReason = ENTITY_EXCLUDED_FIELDS[entityType]?.[rule.field] || null;
      return {
        field: rule.field,
        info: rule,
        isApplicable: !exclusionReason,
        exclusionReason,
      };
    });
}

/**
 * Get compliance categories for a field
 */
export function getFieldCategories(field: string): ComplianceCategory[] {
  return FIELD_GOVERNANCE.find(r => r.field === field)?.complianceCategories || [];
}

/**
 * Check if a field impacts a specific compliance category
 */
export function fieldImpactsCategory(field: string, category: ComplianceCategory): boolean {
  return FIELD_GOVERNANCE.find(r => r.field === field)?.complianceCategories?.includes(category) || false;
}

/**
 * Get all fields that impact a specific compliance category
 */
export function getFieldsByCategory(category: ComplianceCategory): string[] {
  return FIELD_GOVERNANCE
    .filter(rule => rule.complianceCategories?.includes(category))
    .map(rule => rule.field);
}

// ============================================================================
// BACKWARD COMPATIBILITY EXPORTS
// Deprecated aliases - use FIELD_GOVERNANCE and ENTITY_EXCLUDED_FIELDS directly
// ============================================================================

/** @deprecated Use FIELD_GOVERNANCE instead */
export const FIELD_COMPLIANCE_CONFIG: Record<string, FieldGovernanceRule> = Object.fromEntries(
  FIELD_GOVERNANCE.map(rule => [rule.field, rule])
);

/** @deprecated Use ENTITY_EXCLUDED_FIELDS instead */
export const ENTITY_FIELD_EXCLUSIONS = ENTITY_EXCLUDED_FIELDS;
